import type React from "react"
import { useState, useRef, useCallback, forwardRef, useImperativeHandle, useEffect } from "react"
import { DocumentEntityTypesEnum, DocumentService, DocumentTypesEnum } from "@propertelligent/client-api"
import apiHelper from "../../utils/apiHelper"
import "./DocumentPhotoUploader.css"
import SimpleSpinner from "../../components/loader"
import CircularProgress from "../../newComponents/CustomUploader/CircularProgress"

interface UploadedImage {
  id: string
  src: string
  file?: File
  name: string
  documentId?: number // API document ID
}

interface DocumentPhotoUploaderProps {
  entityId: number
  entityType: DocumentEntityTypesEnum
  documentType: DocumentTypesEnum
  onImagesChange?: (images: UploadedImage[]) => void
  onDocumentsStatusChange?: (hasDocuments: boolean) => void
  maxImages?: number
  maxSizeInMB?: number
  label?: string
  required?: boolean
}

export interface DocumentPhotoUploaderRef {
  addBase64Image: (base64: string, filename?: string) => Promise<void>
  refreshDocuments: () => Promise<void>
}

const DocumentPhotoUploader = forwardRef<DocumentPhotoUploaderRef, DocumentPhotoUploaderProps>(
  ({ 
    entityId, 
    entityType, 
    documentType, 
    onImagesChange, 
    onDocumentsStatusChange,
    maxImages = 10, 
    maxSizeInMB = 1,
    label = "Add Fix Photos",
    required = false
  }, ref) => {
    const [images, setImages] = useState<UploadedImage[]>([])
    const [isUploading, setIsUploading] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    // Fetch existing documents from API
    const fetchDocuments = useCallback(async () => {
      if (!entityId) return

      setIsLoading(true)
      setError(null)

      try {
        const response = await apiHelper(
          DocumentService.getApiDocumentList,
          { showNotification: false },
          entityId,
          entityType,
          documentType
        )

        if (response?.data?.success) {
          const documentArray = response.data.success
          const uploadedImages: UploadedImage[] = []

          // Process each document to get base64 data
          for (const doc of documentArray) {
            if (doc.fileName?.toLowerCase().match(/\.(png|jpg|jpeg)$/)) {
              try {
                const base64Response = await apiHelper(
                  DocumentService.getApiDocumentBase641,
                  { showNotification: false },
                  doc.id
                )
                
                if (base64Response?.data?.success) {
                  const base64Data = `data:image/png;base64,${base64Response.data.success}`
                  
                  uploadedImages.push({
                    id: `api-${doc.id}`,
                    src: base64Data,
                    name: doc.fileName || `document-${doc.id}`,
                    documentId: doc.id
                  })
                }
              } catch (error) {
                console.error("Error fetching document base64:", error)
              }
            }
          }

          setImages(uploadedImages)
          onImagesChange?.(uploadedImages)
          onDocumentsStatusChange?.(uploadedImages.length > 0)
        }
      } catch (err) {
        setError("Failed to fetch documents")
        console.error(err)
      } finally {
        setIsLoading(false)
      }
    }, [entityId])

    // Convert base64 to PNG blob
    const base64ToPng = useCallback((base64: string): Promise<File> => {
      return new Promise((resolve) => {
        const canvas = document.createElement("canvas")
        const ctx = canvas.getContext("2d")
        const img = new Image()

        img.onload = () => {
          canvas.width = img.width
          canvas.height = img.height
          ctx?.drawImage(img, 0, 0)

          canvas.toBlob((blob) => {
            if (blob) {
              const file = new File([blob], `image-${Date.now()}.png`, { type: "image/png" })
              resolve(file)
            }
          }, "image/png")
        }

        img.src = base64.startsWith("data:") ? base64 : `data:image/png;base64,${base64}`
      })
    }, [])

    // Upload document to API
    const uploadDocumentToAPI = useCallback(async (file: File): Promise<number | null> => {
      try {
        const documentData = {
          EntityTypeId: entityType,
          EntityId: entityId,
          DocumentTypeId: documentType,
          IsMain: false,
          FileName: file.name,
          Document: file,
        }

        const response = await apiHelper(
          DocumentService.postApiDocument,
          { showNotification: false },
          documentData
        )

        if (response?.data?.success) {
          return response.data.success.id // Return the document ID
        }
        return null
      } catch (error) {
        console.error("Error uploading document:", error)
        throw new Error("Failed to upload document")
      }
    }, [entityId, entityType, documentType])

    // Delete document from API
    const deleteDocumentFromAPI = useCallback(async (documentId: number) => {
      try {
        await apiHelper(
          DocumentService.deleteApiDocument,
          { 
            successMessage: "Document deleted successfully",
            showNotification: false },
          documentId
        )
        return true
      } catch (error) {
        console.error("Error deleting document:", error)
        throw new Error("Failed to delete document")
      }
    }, [])

    // Handle file selection and upload
    const handleFileSelect = useCallback(
      async (files: FileList | null) => {
        if (!files) return

        setIsUploading(true)
        setError(null)

        try {
          // Add a small delay to ensure the uploading state is visible
          await new Promise((resolve) => setTimeout(resolve, 500))

          const newImages: UploadedImage[] = []

          for (let i = 0; i < files.length && images.length + newImages.length < maxImages; i++) {
            const file = files[i]

            // Check file size
            if (file.size > maxSizeInMB * 1024 * 1024) {
              // setError(`File ${file.name} is too large. Maximum size is ${maxSizeInMB}MB.`)
              setError(`File is too large. Maximum size is ${maxSizeInMB}MB.`)
              continue
            }

            // Check file type
            if (!["image/png", "image/jpg", "image/jpeg"].includes(file.type)) {
              setError(`File ${file.name} is not a supported format. Please use PNG, JPG, or JPEG.`)
              continue
            }

            // Upload to API
            const documentId = await uploadDocumentToAPI(file)
            if (!documentId) {
              setError(`Failed to upload ${file.name}`)
              continue
            }

            const imageUrl = URL.createObjectURL(file)
            const newImage: UploadedImage = {
              id: `${Date.now()}-${i}`,
              src: imageUrl,
              file,
              name: file.name,
              documentId: documentId
            }

            newImages.push(newImage)
          }

          if (newImages.length > 0) {
            const updatedImages = [...images, ...newImages]
            setImages(updatedImages)
            onImagesChange?.(updatedImages)
            onDocumentsStatusChange?.(true)
          }

        } catch (err) {
          setError("Failed to upload documents")
          console.error(err)
        } finally {
          setIsUploading(false)
          
          // Reset file input
          if (fileInputRef.current) {
            fileInputRef.current.value = ""
          }
        }
      },
      [images, maxImages, maxSizeInMB, uploadDocumentToAPI, onImagesChange, onDocumentsStatusChange],
    )

    // Handle base64 images from API
    const handleBase64Image = useCallback(
      async (base64: string, filename?: string) => {
        if (images.length >= maxImages) {
          setError(`Maximum ${maxImages} images allowed.`)
          return
        }

        setIsUploading(true)
        setError(null)

        try {
          await new Promise((resolve) => setTimeout(resolve, 500))

          const file = await base64ToPng(base64)
          const documentId = await uploadDocumentToAPI(file)

          if (documentId) {
            const imageUrl = URL.createObjectURL(file)
            const newImage: UploadedImage = {
              id: `base64-${Date.now()}`,
              src: imageUrl,
              file,
              name: filename || `image-${Date.now()}.png`,
              documentId: documentId
            }

            const updatedImages = [...images, newImage]
            setImages(updatedImages)
            onImagesChange?.(updatedImages)
            onDocumentsStatusChange?.(true)
          }
        } catch (error) {
          console.error("Error converting base64 to PNG:", error)
          setError("Error processing base64 image.")
        } finally {
          setIsUploading(false)
        }
      },
      [images, maxImages, base64ToPng, uploadDocumentToAPI, onImagesChange, onDocumentsStatusChange],
    )

    // Remove image and delete from API
    const removeImage = useCallback(
      async (id: string, documentId?: number) => {
        try {
          // Delete from API if documentId exists
          if (documentId) {
            await deleteDocumentFromAPI(documentId)
          }

          const updatedImages = images.filter((img) => {
            if (img.id === id) {
              // Clean up object URL
              URL.revokeObjectURL(img.src)
              return false
            }
            return true
          })

          setImages(updatedImages)
          onImagesChange?.(updatedImages)
          onDocumentsStatusChange?.(updatedImages.length > 0)
        } catch (error) {
          console.error("Error deleting document:", error)
          setError("Failed to delete document")
        }
      },
      [images, deleteDocumentFromAPI, onImagesChange, onDocumentsStatusChange],
    )

    // Handle drag and drop
    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
    }, [])

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        handleFileSelect(e.dataTransfer.files)
      },
      [handleFileSelect],
    )

    // Handle click to upload
    const handleUploadClick = useCallback(() => {
      if (!isUploading && !isLoading) {
        fileInputRef.current?.click()
      }
    }, [isUploading, isLoading])

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        addBase64Image: handleBase64Image,
        refreshDocuments: fetchDocuments,
      }),
      [handleBase64Image, fetchDocuments],
    )

    // Fetch documents on component mount
    useEffect(() => {
      if (entityId) {
        fetchDocuments()
      }
    }, [entityId, fetchDocuments])

    return (
      <div className="photo-uploader">
        <div className="photo-uploader__header">
          <h3>{label}</h3>
          {required && <span className="required-asterisk">*</span>}
        </div>

        {error && (
          <div className="photo-uploader__error">
            {error}
          </div>
        )}

        {isLoading && (
          <div className="photo-uploader__loading">
           <SimpleSpinner myClassName="Icon40" />
          </div>
        )}

        <div className="photo-uploader__grid">
          {/* Upload Area */}
          <div
            className={`photo-uploader__upload-area ${isUploading ? "uploading" : ""} ${isLoading ? "loading" : ""}`}
            onClick={handleUploadClick}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {isUploading ? (
              <>
                <div className="photo-uploader__upload-icon uploading">
                  <CircularProgress size={30} thickness={2} color="blue" progress={isUploading} />
                </div>
                <div className="photo-uploader__upload-text">Uploading</div>
              </>
            ) : (
              <>
                <div className="photo-uploader__upload-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <line
                      x1="12"
                      y1="5"
                      x2="12"
                      y2="19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <line
                      x1="5"
                      y1="12"
                      x2="19"
                      y2="12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="photo-uploader__upload-text">Add Photo</div>
                <div className="photo-uploader__upload-info">
                  Max size: {maxSizeInMB}MB
                  <br />
                  .png, .jpg
                </div>
              </>
            )}
          </div>

          {/* Uploaded Images */}
          {images.map((image) => (
            <div key={image.id} className="photo-uploader__image-container">
              <img src={image.src || "/placeholder.svg"} alt={image.name} className="photo-uploader__image" />
              <button 
                className="photo-uploader__delete-btn" 
                onClick={() => removeImage(image.id, image.documentId)} 
                type="button"
                disabled={isUploading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 6h18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6m3 0V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <line
                    x1="10"
                    y1="11"
                    x2="10"
                    y2="17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <line
                    x1="14"
                    y1="11"
                    x2="14"
                    y2="17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".png,.jpg,.jpeg,image/png,image/jpeg,image/jpg"
          multiple
          onChange={(e) => handleFileSelect(e.target.files)}
          style={{ display: "none" }}
        />
      </div>
    )
  },
)

DocumentPhotoUploader.displayName = "DocumentPhotoUploader"

export default DocumentPhotoUploader